import React from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { defineMessages, FormattedMessage } from 'react-intl';

import * as yup from 'yup';

import { MAXIMUM_COMPANY_NAME_LENGTH } from '../../constants/forms';
import { errorUtils } from '../../utils/errors/errors';
import {
  Input,
  InputState,
  InputType,
  BaseFormInputProps,
} from '../Input/Input';

export enum FormInputCompanyNameLabelName {
  companyNameLabel = 'companyNameLabel',
  companyNameRequiredLabel = 'companyNameRequiredLabel',
}

const messages = defineMessages({
  companyNameLabel: {
    id: 'src.components.FormInputCompanyName.companyNameLabel',
    defaultMessage: 'Company name',
  },
  companyNameRequiredLabel: {
    id: 'src.components.FormInputCompanyName.companyNameRequiredLabel',
    defaultMessage: 'Company name *',
  },
  companyNameEmptyError: {
    id: 'src.components.FormInputCompanyName.companyNameEmptyError',
    defaultMessage: 'Company name is a required field',
  },
  companyNameMaximumLengthExceededError: {
    id: 'src.components.FormInputCompanyName.companyNameMaximumLengthExceededError',
    defaultMessage: 'Company name must be less then {maximumCompanyNameLength} symbols long',
  },
});

export const FORM_INPUT_COMPANY_NAME = 'companyName';

export const companyNameValidationSchema = yup.string().trim()
  .max(MAXIMUM_COMPANY_NAME_LENGTH,'companyNameMaximumLengthExceededError');

export const companyNameRequiredValidationSchema = yup.string().trim()
  .required('companyNameEmptyError')
  .max(MAXIMUM_COMPANY_NAME_LENGTH,'companyNameMaximumLengthExceededError');

const companyNameRestrictionValues = {
  maximumCompanyNameLength: MAXIMUM_COMPANY_NAME_LENGTH,
};

export type FormInputCompanyNameErrors = 'companyNameEmptyError';

export type FormFieldsCompanyName = {
  [FORM_INPUT_COMPANY_NAME]: string;
};

export const formFieldsCompanyNameDefaultValue: FormFieldsCompanyName = {
  [FORM_INPUT_COMPANY_NAME]: '',
};

export type Props = UseFormRegisterReturn & BaseFormInputProps & {
  error?: FieldError;
  isDisabled?: boolean;
  labelName?: FormInputCompanyNameLabelName;
};

export const FormInputCompanyName = React.memo(React.forwardRef((
  props: Props,
  ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement>,
) => {
  const {
    error,
    labelName = FormInputCompanyNameLabelName.companyNameRequiredLabel,
    isDisabled = false,
    ...rest
  } = props;

  return (
    <Input
      inputType={InputType.Input}
      state={
        isDisabled && InputState.Disabled
        || error && InputState.Error
        || undefined
      }
      label={<FormattedMessage {...messages[labelName]} />}
      errorMessage={errorUtils.convertErrorMessage({
        messages,
        error,
        values: companyNameRestrictionValues,
      })}
      type='text'
      {...rest}
      ref={ref}
    />
  );
}));

FormInputCompanyName.displayName = 'FormInputCompanyName';
